import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Bell, MessageSquare, CheckCircle2, QrCode, PenTool, BarChart3,
  Wifi, MapPin, Network, Cpu, Server, Cloud, Boxes,
  Factory, Warehouse, Wrench, Building2, Send, Check,
} from "lucide-react";
import { Reveal, SectionLabel } from "@/components/site/primitives";
import { lenisRef } from "@/lib/lenis";
import { useI18n } from "@/i18n";

const EASE = [0.22, 1, 0.36, 1];

const C = {
  ro: {
    label: "Configurator",
    h1: "Construiește-ți",
    hkw: "soluția în 30 de secunde.",
    sub: "Bifează ce ai nevoie. Îți compunem automat cererea și o trimitem în formularul de contact — tu doar apeși „trimite”.",
    q1: "Ce vrei să poți face?",
    q2: "Unde vrei datele?",
    q3: "Domeniul tău (opțional)",
    features: [
      { id: "push", icon: Bell, t: "Notificări push" },
      { id: "sms", icon: MessageSquare, t: "SMS & alerte" },
      { id: "approve", icon: CheckCircle2, t: "Aprobări din telefon" },
      { id: "qr", icon: QrCode, t: "Scanare QR / coduri" },
      { id: "sign", icon: PenTool, t: "Semnătură digitală" },
      { id: "reports", icon: BarChart3, t: "Rapoarte live" },
      { id: "offline", icon: Wifi, t: "Mod offline" },
      { id: "gps", icon: MapPin, t: "GPS & locație" },
      { id: "erp", icon: Cpu, t: "Integrare ERP / API" },
      { id: "multisite", icon: Network, t: "Multi-sediu / VPN" },
    ],
    data: [
      { id: "onprem", icon: Server, t: "On-premise (serverul meu)" },
      { id: "private", icon: Cloud, t: "Cloud privat" },
      { id: "hybrid", icon: Boxes, t: "Hibrid" },
    ],
    domains: [
      { id: "prod", icon: Factory, t: "Producție & utilaje" },
      { id: "wh", icon: Warehouse, t: "Depozit & logistică" },
      { id: "field", icon: Wrench, t: "Servicii pe teren" },
      { id: "office", icon: Building2, t: "Birou / ERP" },
    ],
    send: "Trimite cererea în formular",
    empty: "Bifează cel puțin o funcționalitate.",
    ok: "Cererea ta a fost pregătită în formular — verific-o și apasă „Trimite mesajul”.",
    subject: "Cerere soluție custom",
    msgIntro: "Bună! Aș dori o soluție custom cu următoarele:",
    msgFeatures: "Funcționalități",
    msgData: "Unde vreau datele",
    msgDomain: "Domeniu",
    msgOutro: "Aștept o propunere. Mulțumesc!",
  },
  en: {
    label: "Configurator",
    h1: "Build your",
    hkw: "solution in 30 seconds.",
    sub: "Tick what you need. We auto-compose your request and drop it into the contact form — you just hit \"send\".",
    q1: "What do you want to be able to do?",
    q2: "Where do you want the data?",
    q3: "Your domain (optional)",
    features: [
      { id: "push", icon: Bell, t: "Push notifications" },
      { id: "sms", icon: MessageSquare, t: "SMS & alerts" },
      { id: "approve", icon: CheckCircle2, t: "Approvals from phone" },
      { id: "qr", icon: QrCode, t: "QR / code scanning" },
      { id: "sign", icon: PenTool, t: "Digital signature" },
      { id: "reports", icon: BarChart3, t: "Live reports" },
      { id: "offline", icon: Wifi, t: "Offline mode" },
      { id: "gps", icon: MapPin, t: "GPS & location" },
      { id: "erp", icon: Cpu, t: "ERP / API integration" },
      { id: "multisite", icon: Network, t: "Multi-site / VPN" },
    ],
    data: [
      { id: "onprem", icon: Server, t: "On-premise (my server)" },
      { id: "private", icon: Cloud, t: "Private cloud" },
      { id: "hybrid", icon: Boxes, t: "Hybrid" },
    ],
    domains: [
      { id: "prod", icon: Factory, t: "Production & machinery" },
      { id: "wh", icon: Warehouse, t: "Warehouse & logistics" },
      { id: "field", icon: Wrench, t: "Field service" },
      { id: "office", icon: Building2, t: "Office / ERP" },
    ],
    send: "Send request to the form",
    empty: "Pick at least one feature.",
    ok: "Your request is ready in the form — review it and hit \"Send message\".",
    subject: "Custom solution request",
    msgIntro: "Hi! I'd like a custom solution with the following:",
    msgFeatures: "Features",
    msgData: "Where I want the data",
    msgDomain: "Domain",
    msgOutro: "Looking forward to a proposal. Thanks!",
  },
};

const Chip = ({ active, icon: Icon, label, onClick, testid }) => (
  <button
    type="button"
    data-testid={testid}
    aria-pressed={active}
    onClick={onClick}
    className={`group inline-flex items-center gap-2 rounded-full border px-4 py-2.5 font-mono text-xs transition-all duration-200 ${
      active
        ? "border-cyan-neon bg-cyan-neon/15 text-white shadow-[0_0_20px_rgba(0,229,255,0.2)]"
        : "border-white/10 bg-ink-800 text-white/60 hover:border-cyan-neon/40 hover:text-white"
    }`}
  >
    <span className={`grid h-4 w-4 place-items-center rounded-full border transition-colors ${active ? "border-cyan-neon bg-cyan-neon text-black" : "border-white/25 text-transparent"}`}>
      <Check size={11} strokeWidth={3} />
    </span>
    <Icon size={15} className={active ? "text-cyan-neon" : "text-white/40"} /> {label}
  </button>
);

export const Configurator = () => {
  const { lang } = useI18n();
  const d = C[lang === "en" ? "en" : "ro"];
  const [features, setFeatures] = useState([]);
  const [dataMode, setDataMode] = useState("onprem");
  const [domain, setDomain] = useState("");

  const toggleFeature = (id) =>
    setFeatures((f) => (f.includes(id) ? f.filter((x) => x !== id) : [...f, id]));

  const submit = () => {
    if (features.length === 0) {
      toast.error(d.empty);
      return;
    }
    const featLabels = d.features.filter((f) => features.includes(f.id)).map((f) => f.t);
    const dataLabel = (d.data.find((x) => x.id === dataMode) || {}).t || "";
    const domainLabel = (d.domains.find((x) => x.id === domain) || {}).t || "";

    let message = `${d.msgIntro}\n\n${d.msgFeatures}: ${featLabels.join(", ")}\n${d.msgData}: ${dataLabel}`;
    if (domainLabel) message += `\n${d.msgDomain}: ${domainLabel}`;
    message += `\n\n${d.msgOutro}`;

    window.dispatchEvent(
      new CustomEvent("dmo:prefill-contact", { detail: { subject: d.subject, message, source: "configurator" } })
    );
    const lenis = lenisRef.current;
    if (lenis) lenis.scrollTo("#contact", { offset: -60, duration: 1.2 });
    else document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" });
    toast.success(d.ok);
  };

  return (
    <section
      id="configurator"
      data-testid="configurator-section"
      className="relative overflow-hidden border-t border-white/10 bg-ink-800"
    >
      <div className="pointer-events-none absolute right-0 top-1/4 h-[26rem] w-[26rem] rounded-full bg-[#7C5CFC]/10 blur-[150px]" />

      <div className="relative mx-auto max-w-5xl px-5 py-24 sm:px-8 sm:py-32">
        <div className="max-w-3xl">
          <Reveal><SectionLabel index="[ CONFIGURATOR ]">{d.label}</SectionLabel></Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-5xl">
              {d.h1} <span className="text-aurora text-aurora-glow">{d.hkw}</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 max-w-2xl font-body text-base leading-relaxed text-white/60">{d.sub}</p>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <div className="card-edge mt-12 rounded-2xl p-6 sm:p-10">
            {/* Q1 features */}
            <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-cyan-neon">{d.q1}</div>
            <div className="mt-4 flex flex-wrap gap-2.5" data-testid="config-features">
              {d.features.map((f) => (
                <Chip
                  key={f.id}
                  testid={`config-feature-${f.id}`}
                  active={features.includes(f.id)}
                  icon={f.icon}
                  label={f.t}
                  onClick={() => toggleFeature(f.id)}
                />
              ))}
            </div>

            {/* Q2 data */}
            <div className="mt-9 font-mono text-[11px] uppercase tracking-[0.2em] text-cyan-neon">{d.q2}</div>
            <div className="mt-4 flex flex-wrap gap-2.5" data-testid="config-data">
              {d.data.map((x) => (
                <Chip
                  key={x.id}
                  testid={`config-data-${x.id}`}
                  active={dataMode === x.id}
                  icon={x.icon}
                  label={x.t}
                  onClick={() => setDataMode(x.id)}
                />
              ))}
            </div>

            {/* Q3 domain */}
            <div className="mt-9 font-mono text-[11px] uppercase tracking-[0.2em] text-cyan-neon">{d.q3}</div>
            <div className="mt-4 flex flex-wrap gap-2.5" data-testid="config-domains">
              {d.domains.map((x) => (
                <Chip
                  key={x.id}
                  testid={`config-domain-${x.id}`}
                  active={domain === x.id}
                  icon={x.icon}
                  label={x.t}
                  onClick={() => setDomain((cur) => (cur === x.id ? "" : x.id))}
                />
              ))}
            </div>

            <motion.button
              data-testid="config-submit"
              onClick={submit}
              whileTap={{ scale: 0.98 }}
              className="btn-shine group mt-10 inline-flex w-full items-center justify-center gap-2 bg-cyan-neon px-8 py-4 font-mono text-sm font-bold uppercase tracking-[0.1em] text-black shadow-[0_0_30px_rgba(0,229,255,0.3)] transition-colors duration-300 hover:bg-white sm:w-auto"
            >
              <Send size={17} /> {d.send}
            </motion.button>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
